import React, { createContext, useState, memo, useEffect } from 'react';
import { toast } from 'react-toastify';
import { UserFormType } from '../views/creatorOnBoarding/signup.types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getProfileByUserId, getProfileByUsername } from '../utils/api';
import { useAuth } from '../hooks/useAuth';

const enableDevTools = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';

// This context holds all data for creatorProfileData only
interface ProfileContextType {
  creatorProfileData: UserFormType | null;
  fetchProfileDataFromUserName: (
    userName: string,
    authUserId: string,
    getDetails?: boolean,
    onNotFound?: () => void,
  ) => Promise<void>;
  fetchProfileDataFromUserId: (
    userId: string,
    authUserId: string,
    getDetails?: boolean,
    onNotFound?: () => void,
  ) => Promise<any>;
  isLoadingProfile: boolean;
  forceRefetch: () => Promise<void>;
}

export const ProfileContext = createContext<ProfileContextType>({
  creatorProfileData: null,
  fetchProfileDataFromUserName: async () => { return; },
  fetchProfileDataFromUserId: async () => { return; },
  isLoadingProfile: false,
  forceRefetch: async () => {return;}
});

export const ProfileProvider = ({ children}: { children: React.ReactNode;}) => {
  const [searchParams, setSearchParams] =
    useState<{
      userName?: string,
      userId?: string,
      getDetails: boolean,
      authUserId?: string,
      onNotFound?: () => void,
    }>(null);

  const { authUser } = useAuth();

  const queryClient = useQueryClient();

  useEffect(() => {
    // if authUser data is changed and creatorProfileData is authUser,
    // update creatorProfileData
    if ((authUser?.uid && authUser?.uid === creatorProfileData?.userId)
      || (creatorProfileData && !creatorProfileData.userId)) {
      forceRefetch();
    }
  }, [authUser])

  // function to handle fetch depending on what's in searchParams
  const getProfileData = async () => {
    if (searchParams?.userId) {
      return getProfileByUserId(
        searchParams?.authUserId,
        searchParams?.userId,
        searchParams?.getDetails,
        false
      )
    } else {
      return getProfileByUsername(
        searchParams?.authUserId,
        searchParams?.userName,
        searchParams?.getDetails,
        false
      )
    }
  }

  const validateUserData = (data) => {
    // placeholder, should validate userdata
    return true;
  }

  const {
    data: creatorProfileData,
    isLoading: isLoadingProfile
  } = useQuery({
    queryKey: ['profile-data',
      searchParams?.userName,
      searchParams?.userId,
      searchParams?.getDetails,
    ],
    queryFn: async () => {
      if (!searchParams || (!searchParams.userName && !searchParams.userId)) {
        if (searchParams?.onNotFound) {
          searchParams.onNotFound();
        }
        return null;
      }
      const isFirstLogin = authUser?.metadata?.creationTime === authUser?.metadata?.lastSignInTime;
      const shouldShowToast = !isFirstLogin;

      return getProfileData()
        .then((resp) => {
          if (resp.ok) {
            return resp.json()
          } else {
            if (shouldShowToast) {
              toast.error('User Not Found')
            }
            if (searchParams.onNotFound) {
              searchParams.onNotFound();
            }
            return null;
          }
        })
        .then((data: UserFormType) => {
          if (!validateUserData(data)) {
            if (shouldShowToast) {
              toast.error('User Not Found')
            }
            if (searchParams.onNotFound) {
              searchParams.onNotFound();
            }
            return null;
          }
          data.normalizedUserName = data.userName.toLowerCase();
          return data as UserFormType
        })
        .catch((error) => {
          if (shouldShowToast) {
            toast.error('User Not Found')
          }
          if (searchParams.onNotFound) {
            searchParams.onNotFound();
          }
          return null
        })
    },
    staleTime: 1000*60*60*24, // 1 day
    enabled: !!searchParams,
  })

  useEffect(() => {
    if (enableDevTools) { console.log('Creator Profile Data: ', creatorProfileData )}
  }, [creatorProfileData])

  // fetch user data from the username
  const fetchProfileDataFromUserName = async (
    userName: string,
    authUserId: string,
    getDetails: boolean = false,
    onNotFound?: () => void, // what the function should do when user isn't found
  ) => {
    setSearchParams({
      userName,
      authUserId,
      getDetails,
      onNotFound, // what the function should do when user isn't found
    })
  };

  // fetch creator data from userId
  const fetchProfileDataFromUserId = async (
    userId: string,
    authUserId: string,
    getDetails: boolean = false,
    onNotFound?: () => void, // what the function should do when user isn't found
  ) => {
    setSearchParams({
      userId,
      authUserId,
      getDetails,
      onNotFound, // what the function should do when user isn't found
    })
  };

  const forceRefetch = async () => {
    // invalidates the old query data and refetches it
    queryClient.invalidateQueries({
      queryKey: ['profile-data',
        searchParams?.userName,
        searchParams?.userId,
        searchParams?.getDetails,
      ],
      refetchType: 'all'
    })
  }

  return (
    <ProfileContext.Provider
      value={{
        creatorProfileData,
        fetchProfileDataFromUserName,
        fetchProfileDataFromUserId,
        isLoadingProfile,
        forceRefetch
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export default memo(ProfileProvider);
